"use client";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Address, getAddress } from "viem";

import { Redeemer } from "#/services/firebase/firebase.types";
import { useAccount } from "#/store/wallet/account/account";
import {
  RedeemersTradeStats,
  ReferralsDetails,
  ReferralsSummary,
} from "#/types/referrals";

import { fetchTradeStats } from "../kyan.service";
import { FetchTradeStatsRequest, FetchTradeStatsResponse } from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

const createDefaultDates = () => {
  const from = moment().subtract(364, "days").format("YYYY-MM-DD");
  const to = moment().format("YYYY-MM-DD");

  return { from, to };
};

const useTradeStatsBaseQuery = <TData = FetchTradeStatsResponse>(
  params: FetchTradeStatsRequest | null,
  select?: (data: FetchTradeStatsResponse) => TData,
) =>
  useQuery({
    queryKey: ["fetchTradeStats", params],
    queryFn: () =>
      defaultApiResponseHandler<
        FetchTradeStatsRequest,
        FetchTradeStatsResponse
      >({
        serviceName: "Fetch Trade Stats",
        payload: params!,
        service: fetchTradeStats,
      }),
    enabled: Boolean(params),
    gcTime: 0,
    retry: false,
    select,
  });

export const useTradeStats = (options?: Partial<FetchTradeStatsRequest>) => {
  const { account } = useAccount();

  const params = account
    ? ({
        ...createDefaultDates(),
        accounts: account,
        ...options,
      } as FetchTradeStatsRequest)
    : null;

  return useTradeStatsBaseQuery(params);
};

const emptyReferralsSummary: ReferralsSummary = {
  totalVolume: 0,
  totalReferrals: 0,
  activeReferrals: 0,
};

const createReferralsDetailsPlaceholder = (
  account: Address,
): ReferralsDetails[] => [
  {
    address: account,
    date: new Date(0),
    perpsVolume: 0,
    optionsVolume: 0,
    totalVolume: 0,
    complexityScore: 0,
    referralReward: 0,
    isYours: true,
  },
];

export const useRedeemersTradeStats = (
  redeemers: Redeemer[],
  options?: Partial<FetchTradeStatsRequest>,
): { data: RedeemersTradeStats; isLoading: boolean } => {
  const { account } = useAccount();

  const params = redeemers.length
    ? ({
        ...createDefaultDates(),
        accounts: redeemers.map(({ usedBy }) => getAddress(usedBy)).join(","),
        ...options,
      } as FetchTradeStatsRequest)
    : null;

  const selector = (result: FetchTradeStatsResponse) => {
    const groupsByAccount = new Map(
      (result?.groups ?? []).map((g) => [g.account, g]),
    );

    const referralsDetails = redeemers
      .map((redeemer) => {
        const item = groupsByAccount.get(getAddress(redeemer.usedBy));

        const perpsVolume = item?.perps?.volume ?? 0;
        const optionsVolume = item?.options?.strike_notional ?? 0;
        const totalVolume = perpsVolume + optionsVolume;

        return {
          address: redeemer.usedBy,
          date: redeemer.usedAt,
          perpsVolume,
          optionsVolume,
          totalVolume,
          complexityScore: 10, // TODO: add complexity score
          referralReward: NaN, // TODO: add referral reward
          isYours: getAddress(redeemer.usedBy) === account,
        };
      })
      .sort((a, b) => moment(a.date).diff(moment(b.date)));

    const others = referralsDetails.filter((r) => !r.isYours);
    const totalVolume = others.reduce((acc, r) => acc + r.totalVolume, 0);
    const activeReferrals = (result?.groups ?? []).filter(
      (g) => g.account !== account,
    ).length;

    return {
      referralsSummary: {
        totalVolume,
        totalReferrals: redeemers.length - 1,
        activeReferrals,
      },
      referralsDetails,
    };
  };

  const { data, isLoading } = useTradeStatsBaseQuery(params, selector);

  return {
    data: data ?? {
      referralsSummary: emptyReferralsSummary,
      referralsDetails: createReferralsDetailsPlaceholder(account as Address),
    },
    isLoading,
  };
};
